import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import { columnLabel, formatColumnValue } from './formatColumnValue';
import { formatExcludeTerm } from './listInputs';
import { growthTone } from './metricTone';
import type {
    Column,
    EntityDataInput,
    EntityDataOutput,
    RawRow,
    RowBadge,
    TableRowModel,
    TableTitleModel,
} from '../types';

type MetaByKey = Parameters<typeof formatColumnValue>[3];

/** Per-source inputs to the shared two-column (volume + active-sort) builder. */
export interface ColumnSpec {
    volumeKey: string;
    defaultSortKey: string;
    /** Second column shown when the active sort IS the volume column. */
    volumeGrowthFallback: string;
    /** When set, an order_by outside this set falls back to defaultSortKey. */
    sortableKeys?: Set<string>;
    /** Label source; defaults to the popularity map. */
    metaByKey?: MetaByKey;
}

export const POP_INDEX_COLUMN_SPEC: ColumnSpec = {
    volumeKey: POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month?.key,
    defaultSortKey: POPULARITY_METADATA_BY_KEY.increase_YoY?.key,
    volumeGrowthFallback: POPULARITY_METADATA_BY_KEY.YoY?.key,
};

/** Volume column + the active-sort column, flagging whichever is the real sort. */
export function buildColumns(
    orderBy: string | undefined,
    spec: ColumnSpec = POP_INDEX_COLUMN_SPEC,
): Column[] {
    const {
        volumeKey,
        defaultSortKey,
        volumeGrowthFallback,
        sortableKeys,
        metaByKey,
    } = spec;
    const sortKey =
        orderBy != null && (!sortableKeys || sortableKeys.has(orderBy))
            ? orderBy
            : defaultSortKey;
    const growthKey = sortKey === volumeKey ? volumeGrowthFallback : sortKey;
    return [volumeKey, growthKey].map((key) => ({
        key,
        label: columnLabel(key, metaByKey),
        isSort: key === sortKey,
        isGrowth: key !== volumeKey,
    }));
}

export function buildRows(
    output: EntityDataOutput,
    columns: Column[],
    nameKey: string,
    metaByKey?: Parameters<typeof formatColumnValue>[3],
    subtextFn?: (rawRow: RawRow | undefined) => string | undefined,
    nameHrefFn?: (rawRow: RawRow | undefined) => string | undefined,
    // Per-row pill (e.g. the creators "Investment opportunity" badge).
    badgeFn?: (rawRow: RawRow | undefined) => RowBadge | undefined,
): TableRowModel[] {
    const formattedRows = output?.result?.metrics;
    if (!Array.isArray(formattedRows)) {
        throw new Error(
            'inline table: get_entity_data output is missing result.metrics (expected an array)',
        );
    }
    const rawRows = output?._rows ?? [];
    return formattedRows.map((formattedRow, i) => {
        const rawRow = rawRows[i];
        const name = formattedRow?.[nameKey]?.value ?? '—';
        const cells = columns.map((col) => {
            const value = formatColumnValue(
                col.key,
                formattedRow,
                rawRow,
                metaByKey,
            );
            return {
                value,
                tone: col.isGrowth ? growthTone(value) : undefined,
            };
        });
        // subtextFn / nameHrefFn / badgeFn are opt-in per widget — pop_index
        // tables pass none, so the cluster/brand subtext, name links, and badges
        // never leak into them.
        return {
            name,
            excludeName: formattedRow?.[nameKey]?.value,
            index: i,
            nameHref: nameHrefFn?.(rawRow),
            lifeCycle: rawRow?.life_cycle,
            subtext: subtextFn?.(rawRow),
            badge: badgeFn?.(rawRow),
            cells,
        };
    });
}

export const parentName = (output: EntityDataOutput): string | null =>
    output?._entity?.name ?? null;

/**
 * A pinned list's title members from the RESOLVED names, never `pinned_count`,
 * so an unresolved id can't inflate the "N more". Empty when nothing resolved.
 */
export function pinnedMembers(
    names: ReadonlyArray<string | null | undefined>,
): Pick<TableTitleModel, 'members'> {
    const resolved = names.filter((n): n is string => Boolean(n));
    return resolved.length ? { members: resolved } : {};
}

/** The terms the backend actually filtered on, which are not always the ones
 * asked for: `_normalize_exclude_terms` lowercases, dedupes, and '#'-prefixes
 * an exact TikTok hashtag. Falls back to the request for a payload carrying no
 * echo — an older one, or the optimistic stand-in, which drops it so the count
 * moves with the row. */
export const excludedTerms = (
    input: EntityDataInput,
    output?: EntityDataOutput,
): Pick<TableTitleModel, 'excluded'> => {
    const applied = output?.result?.applied_exclusions ?? input?.exclude;
    return applied?.length ? { excluded: applied.map(formatExcludeTerm) } : {};
};

/** The tables' entry: pinned lists only, names from the summary rows' name
 * cell (the same value `buildRows` puts on `rows[i].name`). */
export const pinnedTitleParts = (
    output: EntityDataOutput,
    nameKey: string,
): Pick<TableTitleModel, 'members'> =>
    output?.result?.pinned_count
        ? pinnedMembers(
              (output.result.metrics ?? []).map((row) => row?.[nameKey]?.value),
          )
        : {};
